
    import has from 'lodash/has';
    import { mapState, mapGetters, mapActions } from 'vuex';
    import { ValidationObserver } from 'vee-validate';
    import { faSpinner as fasSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { jobData } from '@/utils/jobData';
    import datalayer from '@/utils/datalayer';

    import LoginMixin from '@/components/Utils/LoginMixin';
    import ApplyMixin from '@/components/JobView/Apply/ApplyMixin';
    import EmailDebounceMixin from '@/components/AuthCommon/EmailDebounceMixin.vue';

    import ApplyPersonalInfo from '@/components/JobView/Apply/ApplyPersonalInfo';
    import ApplyCoverLetter from '@/components/JobView/Apply/ApplyCoverLetter';
    import ApplyResume from '@/components/JobView/Apply/ApplyResume';
    import ApplyFormFooter from '@/components/JobView/Apply/ApplyFormFooter';
    import C1Spinner from '@/C1Components/C1Spinner';

    export default {
        name: 'ApplyFormNewUser',

        components: {
            ValidationObserver,
            C1Spinner,
            ApplyPersonalInfo,
            ApplyCoverLetter,
            ApplyResume,
            ApplyFormFooter,
            FontAwesomeIcon,
        },

        mixins: [LoginMixin, ApplyMixin, EmailDebounceMixin],

        data() {
            return {
                loader: {
                    apply: false,
                },
                icons: {
                    fasSpinner,
                },
            };
        },

        computed: {
            ...mapState({
                $_job: state => state.job.job,
                $_me: state => state.me.user,
                $_jobApplication: state => state.jobApplication,
                $_isEmailValidating: state => state.auth.isEmailValidating,
            }),

            ...mapGetters('jobApplication', {
                $_applyEmail: 'applyEmail',
            }),
        },

        methods: {
            ...mapActions('auth', {
                $_authRegister: 'register',
            }),

            async handleRegister() {
                if (!this.isEmailForced) {
                    await this.debounceValidateEmail(this.$_applyEmail);
                }
                if (this.isEmailForced || this.isEmailValid) {
                    this.register();
                }
            },

            async register() {
                const validation = await this.$refs.formNewUser.validate();
                if (!validation) {
                    return false;
                }

                const data = {
                    channel_source: 'jobviewV3-onsite',
                    email: this.$_applyEmail,
                    job_uuid: this.$_job.uuid,
                    recaptcha_disable: 1,
                };

                if (this.$refs.personalInfo.firstName) {
                    data.first_name = this.$refs.personalInfo.firstName;
                }

                if (this.$refs.personalInfo.lastName) {
                    data.last_name = this.$refs.personalInfo.lastName;
                }

                if (this.$refs.personalInfo.contactNumber) {
                    data.contact_number = this.$refs.personalInfo.contactNumber;
                }

                if (has(this.$refs.coverLetter, 'selectedCoverLetter.title')) {
                    data.cover_letter_title = this.$refs.coverLetter.selectedCoverLetter.title;
                }

                if (has(this.$refs.coverLetter, 'selectedCoverLetter.body')) {
                    data.cover_letter_body = this.$refs.coverLetter.selectedCoverLetter.body;
                }

                if (has(this.$refs.resume, 'selectedResume.upload.uuid')) {
                    data.resume_file = this.$refs.resume.selectedResume;
                }

                // Captcha by pass
                if (has(this.$route.query, 'recaptcha_bypass_token')) {
                    data.recaptcha_bypass_token = this.$route.query.recaptcha_bypass_token;
                }

                // Set resume review
                if (this.$_jobApplication.is_resume_review) {
                    data.top_resume = 1;
                }

                this.loader.apply = true;

                data.site_position = 'apply_form_new_user';
                data.current_url = window.location.href;

                this.$_register(data).then((response) => {
                    const data = {
                        event: 'seeker_account_creation',
                        site_position: 'job_apply',
                        method: 'email',
                        ...jobData(this.$_job),
                        seeker_id: response.data.id,
                        seeker_hash: response.data.email_hash,
                    };

                    this.$_pushEvent(data);
                    datalayer(data);

                    if (data.resume_file) {
                        const data = {
                            event: 'seeker_resume_upload',
                            site_position: 'application_form',
                            resume_review: false,
                            seeker_id: this.$_me.id,
                            seeker_hash: this.$_me.email_hash,
                        };

                        this.$_pushEvent(data);
                        datalayer(data);
                    }

                    this.afterApply(response.apply);
                }).finally(() => {
                    this.loader.apply = false;
                });
            },

            $_register(data) {
                return this.$_authRegister(data).then((response) => {
                    this.$_storeUserAfterRegister(response);
                    return response;
                });
            },
        },
    };
